import { useEffect, useState, useMemo } from 'react';
import { supabase } from '@/core/database/supabase';
import { defaultSettingsForm, type SettingsActivity, type SettingsFormState, type VersionInfo } from '../components';

type Row = Record<string, any>;

function valueFrom(row: Row | null, key: string, fallback: any) {
  if (!row) return fallback;
  const nested = row.settings && typeof row.settings === 'object' ? row.settings : {};
  return row[key] ?? nested[key] ?? fallback;
}

function mapSettings(company: Row | null): SettingsFormState {
  return {
    ...defaultSettingsForm,
    systemName: company?.name || defaultSettingsForm.systemName,
    systemDescription: company?.description || defaultSettingsForm.systemDescription,
    logoPath: company?.logo_path || '',
    timezone: valueFrom(company, 'timezone', defaultSettingsForm.timezone),
    language: valueFrom(company, 'language', defaultSettingsForm.language),
    dateFormat: valueFrom(company, 'date_format', defaultSettingsForm.dateFormat),
    defaultStatus: valueFrom(company, 'default_work_status', defaultSettingsForm.defaultStatus),
    upcomingDays: valueFrom(company, 'upcoming_days', defaultSettingsForm.upcomingDays),
    overdueReminder: valueFrom(company, 'overdue_reminder', defaultSettingsForm.overdueReminder),
    reminderTime: valueFrom(company, 'reminder_time', defaultSettingsForm.reminderTime),
    allowDuplicateAssignments: Boolean(valueFrom(company, 'allow_duplicate_assignments', defaultSettingsForm.allowDuplicateAssignments)),
    autoStatusUpdate: Boolean(valueFrom(company, 'auto_status_update', defaultSettingsForm.autoStatusUpdate)),
    maxFileSize: valueFrom(company, 'max_file_size', defaultSettingsForm.maxFileSize),
    allowedFileTypes: valueFrom(company, 'allowed_file_types', defaultSettingsForm.allowedFileTypes),
    storageUsedGb: Number(valueFrom(company, 'storage_used_gb', defaultSettingsForm.storageUsedGb)),
    storageLimitGb: 1, // Enforced for free tier
    showQuickTips: Boolean(valueFrom(company, 'show_quick_tips', defaultSettingsForm.showQuickTips)),
    darkMode: Boolean(valueFrom(company, 'dark_mode', defaultSettingsForm.darkMode)),
    collapseSidebar: Boolean(valueFrom(company, 'collapse_sidebar', defaultSettingsForm.collapseSidebar)),
  };
}

function mapActivity(logs: Row[]): SettingsActivity[] {
  return logs.map((log) => ({
    id: log.id,
    actorName: log.actor?.full_name || 'Hệ thống',
    summary: log.summary || log.action_code || 'đã cập nhật cài đặt',
    createdAt: log.created_at || null,
  }));
}

async function safeMaybeSingle(query: any, label: string) {
  const { data, error } = await query;
  if (error) {
    console.warn(`[useSystemSettings] ${label} fallback:`, error);
    return null;
  }
  return data || null;
}

async function safeList(query: any, label: string) {
  const { data, error } = await query;
  if (error) {
    console.warn(`[useSystemSettings] ${label} fallback:`, error);
    return [];
  }
  return Array.isArray(data) ? data : [];
}

async function loadAuditLogs(companyId: string, userId?: string): Promise<Row[]> {
  const withCompany = await supabase
    .from('audit_logs')
    .select('id, summary, action_code, module_code, created_at, actor:user_profiles!actor_user_id(full_name, email)')
    .eq('company_id', companyId)
    .order('created_at', { ascending: false })
    .limit(5);

  if (!withCompany.error) return withCompany.data || [];

  console.warn('[useSystemSettings] audit_logs company fallback:', withCompany.error);
  if (!userId) return [];
  return safeList(
    supabase
      .from('audit_logs')
      .select('id, summary, action_code, module_code, created_at, actor:user_profiles!actor_user_id(full_name, email)')
      .eq('actor_user_id', userId)
      .order('created_at', { ascending: false })
      .limit(5),
    'audit_logs',
  );
}

export function useSystemSettings(companyId: string | undefined, userId: string | undefined) {
  const [form, setForm] = useState<SettingsFormState>(defaultSettingsForm);
  const [activities, setActivities] = useState<SettingsActivity[]>([]);
  const [version, setVersion] = useState<VersionInfo>({ version: 'v2.3.1', lastUpdate: null });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    async function loadData() {
      if (!companyId) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      const [company, logs, storageRpc] = await Promise.all([
        safeMaybeSingle(supabase.from('companies').select('*').eq('id', companyId).maybeSingle(), 'companies'),
        loadAuditLogs(companyId, userId),
        supabase.rpc('fn_get_storage_usage')
      ]);

      if (!mounted) return;

      const mappedForm = mapSettings(company);

      // Calculate real storage in GB (from bytes)
      if (!storageRpc.error && storageRpc.data) {
        const bytes = Number(storageRpc.data);
        mappedForm.storageUsedGb = Number((bytes / (1024 * 1024 * 1024)).toFixed(2));
      }

      setForm(mappedForm);
      setActivities(mapActivity(logs));
      setVersion({
        version: (import.meta.env.VITE_APP_VERSION as string) || 'v1.0.0',
        lastUpdate: company?.updated_at || company?.created_at || null,
      });
      setIsLoading(false);
    }

    loadData();
    return () => {
      mounted = false;
    };
  }, [companyId, userId]);

  const canSave = useMemo(() => Boolean(companyId && form.systemName.trim()), [companyId, form.systemName]);

  const updateForm = (patch: Partial<SettingsFormState>) => {
    setForm((current) => ({ ...current, ...patch }));
  };

  return {
    form,
    activities,
    version,
    isLoading,
    canSave,
    updateForm,
    setVersion
  };
}
